import React, { useContext } from 'react';
import styled from 'styled-components';
import { Row, Col, Skeleton } from 'antd';
import TagLogo from '../tag-logo';
import { Strong } from '../styles';
import { device } from '../../lib/theme';
import { AdsContext, getAd } from '../../lib/context/ads';
import { SettingContext } from '../../lib/context/settings';

const ListBlock = styled.ul`
  padding: 0;
  list-style: none;
  margin-bottom: 24px;

  > li {
    margin: 4px 0 0;
  }
  > li a {
    color: #2f2f2f;
    font-size: 14px;
    text-decoration: none;

    &:hover {
      color: #00529b;
    }
  }
`;

const ListBlockHeader = styled.span`
  font-size: 16px;
  font-weight: bold;
  color: #2f2f2f;
`;

const Slogan = styled.p`
  font-size: 20px;
  font-weight: bold;
  color: #2f2f2f;
  display: inline-block;
`;

const Copyright = styled(Row)`
  font-family: 'CG-regular';
  font-size: 12px;
  margin-top: 32px;

  a {
    font-family: 'CG-bold';
    color: #003366;
  }
`;

const Social = styled(Col)`
  img {
    height: 24px;
  }

  a {
    margin-right: 16px;
    &:last-of-type {
      margin-right: 0;
    }
  }

  margin-top: 16px;
  margin-bottom: 32px;
`;

const FooterContainer = styled.div`
  border-top: 4px solid #ed1b2e;
  background-color: #efefef;
  padding: 0 0 32px;
`;

const BannerAdContent = styled.div`
  p {
    margin: 8px 0 0;
    line-height: 1.2;
    font-size: 14px;

    @media ${device.tablet} {
      font-size: 16px;
    }
  }
`;

const Footer: React.FC<{
  noAds?: boolean;
  noNav?: boolean;
  noSocial?: boolean;
  noSlogan?: boolean;
}> = ({ noAds = false, noNav = false, noSocial = false, noSlogan = false }) => {
  const { setSetting, settings } = useContext(SettingContext);
  const { ads, loading: adsLoading } = useContext(AdsContext);

  const bannerAd = getAd(ads, { category: 'Footer Banner' });
  const footerAd = getAd(ads, { category: 'Footer Side' });

  const goToMemberView = () => {
    setSetting('member', !settings.member);
  };

  const Banner = adsLoading ? (
    <Row style={{ backgroundColor: '#343434', color: '#fff' }}>
      <Col
        xs={{ span: 22, offset: 1 }}
        md={{ span: 20, offset: 2 }}
        style={{
          height: 106,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <div style={{ width: '30%' }}>
          <Skeleton
            active
            avatar={{ shape: 'square' }}
            paragraph={{ rows: 1, width: 250 }}
            title={{ width: 125 }}
          />
        </div>
      </Col>
    </Row>
  ) : bannerAd && bannerAd[0] ? (
    <a href={bannerAd[0].url}>
      <Row style={{ backgroundColor: '#343434', color: '#fff' }}>
        <Col
          xs={{ span: 22, offset: 1 }}
          md={{ span: 20, offset: 2 }}
          style={{
            height: 106,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <img
            src={bannerAd[0].featuredImage}
            style={{ marginRight: 32, height: 60 }}
          />
          <BannerAdContent>
            <Strong color="white" uppercase marginBottom={0}>
              {bannerAd[0].title}
            </Strong>
            <span
              style={{ fontSize: 18 }}
              dangerouslySetInnerHTML={{ __html: bannerAd[0].content }}
            ></span>
          </BannerAdContent>
        </Col>
      </Row>
    </a>
  ) : null;

  return (
    <>
      {noAds ? null : Banner}
      <FooterContainer>
        <Row style={{ position: 'relative', height: 100 }}>
          <Col xs={{ span: 22, offset: 1 }} lg={{ span: 20, offset: 2 }}>
            <TagLogo small style={{ marginRight: 32, position: 'relative' }} />
            {!noSlogan ? <Slogan>Expect Something More</Slogan> : null}
          </Col>
        </Row>
        {noNav ? null : (
          <Row style={{ paddingTop: 48 }}>
            <Col
              xs={{ span: 22, offset: 1 }}
              md={{ span: 14 }}
              lg={{ span: 17, offset: 2 }}
            >
              <Row gutter={[16, 16]}>
                <Col xs={{ span: 12, offset: 0 }} lg={{ span: 8 }} flex={1}>
                  <ListBlockHeader>Emergency Road Service</ListBlockHeader>
                  <ListBlock>
                    <li>
                      <a href="tel:800-111-4357">800-AAA-HELP</a>
                    </li>
                    <li>
                      <a href="https://roadside.acg.aaa.com/">
                        Request Service Online
                      </a>
                    </li>
                    <li>
                      <a href="https://member.acg.aaa.com/car/automotive/reimbursement-form.html">
                        ERS Reimbursement Forms
                      </a>
                    </li>
                  </ListBlock>

                  <ListBlockHeader>Member Service Center</ListBlockHeader>
                  <ListBlock>
                    <li>
                      <a href="tel:866-566-8635">866-566-8635</a>
                    </li>
                    <li>
                      <a href="#" onClick={goToMemberView}>
                        Non-Member?
                      </a>
                    </li>
                  </ListBlock>
                </Col>
                <Col xs={{ span: 12, offset: 0 }} lg={{ span: 4 }}>
                  <ListBlockHeader>My Account</ListBlockHeader>
                  <ListBlock>
                    <li>
                      <a href="https://members.carolinas.aaa.com/LoginPage">
                        Sign In
                      </a>
                    </li>
                    <li>
                      <a href="https://members.carolinas.aaa.com/CreateAnAccount">
                        Register
                      </a>
                    </li>
                    <li>
                      <a href="https://members.carolinas.aaa.com/ResetCommunityPasswordAAA">
                        Forgot Password
                      </a>
                    </li>
                  </ListBlock>

                  <ListBlockHeader>Contact</ListBlockHeader>
                  <ListBlock>
                    <li>
                      <a href="https://carolinas.aaa.com/about/contact-us/">
                        Contact Us
                      </a>
                    </li>
                    <li>
                      <a href="http://locator.carolinas.aaa.com/">
                        Find A Location
                      </a>
                    </li>
                  </ListBlock>
                </Col>
                <Col xs={{ span: 12, offset: 0 }} lg={{ span: 4 }}>
                  <ListBlockHeader>Car Care</ListBlockHeader>
                  <ListBlock>
                    <li>
                      <a href="http://locator.carolinas.aaa.com/?type=carcare">
                        Reserve Appointment
                      </a>
                    </li>
                    <li>
                      <a href="http://locator.carolinas.aaa.com/?type=carcare">
                        Find A Location
                      </a>
                    </li>
                    <li>
                      <a href="https://carolinas.aaa.com/automotive/carcare/services/">
                        Car Care Services
                      </a>
                    </li>
                  </ListBlock>

                  <ListBlockHeader>Insurance</ListBlockHeader>
                  <ListBlock>
                    <li>
                      <a href="https://www.aaacarolinasinsurancesolutions.com/quote/">
                        Get A Quote
                      </a>
                    </li>
                    <li>
                      <a href="https://carolinas.aaa.com/tmic/manage-policy/">
                        Manage My Policy
                      </a>
                    </li>
                    <li>
                      <a href="https://carolinas.aaa.com/insurance/products/">
                        Insurance Products
                      </a>
                    </li>
                  </ListBlock>
                </Col>
                <Col xs={{ span: 12, offset: 0 }} lg={{ span: 4 }}>
                  <ListBlockHeader>About AAA</ListBlockHeader>
                  <ListBlock>
                    <li>
                      <a href="https://carolinas.aaa.com/emergency/coronavirus/">
                        COVID-19
                      </a>
                    </li>
                    <li>
                      <a href="https://carolinas.aaa.com/about/">Our Story</a>
                    </li>
                    <li>
                      <a href="https://carolinas.aaa.com/about/history/">
                        AAA History
                      </a>
                    </li>
                    <li>
                      <a href="https://carolinas.aaa.com/about/careers/">
                        Careers
                      </a>
                    </li>
                    <li>
                      <a href="https://www.acgfoundation.org/">
                        ACG Foundation
                      </a>
                    </li>
                    <li>
                      <a href="https://carolinas.aaa.com/about/#sustainability">
                        Sustainability
                      </a>
                    </li>
                  </ListBlock>

                  <ListBlockHeader>Publications</ListBlockHeader>
                  <ListBlock>
                    <li>
                      <a href="https://living.acg.aaa.com/">AAA Living</a>
                    </li>
                  </ListBlock>
                </Col>

                <Col xs={{ span: 12, offset: 0 }} lg={{ span: 4 }}>
                  <ListBlockHeader>News & Events</ListBlockHeader>
                  <ListBlock>
                    <li>
                      <a href="https://carolinas.aaa.com/about/media-center/">
                        Newsroom
                      </a>
                    </li>
                    <li>
                      <a href="https://carolinas.aaa.com/about/media-center/">
                        Press Releases
                      </a>
                    </li>
                    <li>
                      <a href="http://aaatalks.carolinas.aaa.com/travel/events/">
                        Store Events
                      </a>
                    </li>
                  </ListBlock>
                </Col>
              </Row>
            </Col>
            {noAds ? null : (
              <Col
                xs={{ span: 22, offset: 1 }}
                md={{ span: 9, offset: 0 }}
                lg={{ span: 5, offset: 0 }}
                style={{ backgroundColor: 'white', padding: '0 32px 32px' }}
              >
                {adsLoading ? (
                  <>
                    <div style={{ paddingTop: 60 }}>
                      <Skeleton.Avatar shape="square" active />
                      <Skeleton active />
                    </div>
                  </>
                ) : footerAd && footerAd[0] ? (
                  <a href={footerAd[0].url} style={{ color: '#333' }}>
                    <img
                      src={footerAd[0].featuredImage}
                      style={{ width: 160, display: 'block', marginBottom: 16 }}
                    />
                    <div
                      dangerouslySetInnerHTML={{ __html: footerAd[0].content }}
                    ></div>
                  </a>
                ) : null}
              </Col>
            )}
          </Row>
        )}
        <Copyright>
          <Col
            xs={{ span: 21, offset: 1 }}
            lg={noSocial ? { span: 12, offset: 2 } : { span: 14, offset: 2 }}
          >
            <p style={{ marginBottom: 2 }}>
              © 2022 AAA. Serving AAA Members and Residents in Colorado,
              Florida, Georgia, Iowa, Michigan, Nebraska, North Carolina, North
              Dakota, South Carolina, Tennessee, and Wisconsin; and in select
              counties in Illinois, Indiana, Minnesota. All rights reserved.
            </p>
            <a href="https://carolinas.aaa.com/privacy-policy/">
              Privacy Policy
            </a>{' '}
            | <a href="http://aaa.com/stop">Find Another AAA Club</a>
          </Col>
          {!noSocial ? (
            <Social xs={{ span: 21, offset: 1 }} lg={{ span: 6, offset: 2 }}>
              <a href="https://www.facebook.com/aaacarolinas">
                <img src="/icons/fb@2x.png" />
              </a>
              <a href="https://twitter.com/aaacarolinas">
                <img src="/icons/twitter@2x.png" />
              </a>
              <a href="https://www.instagram.com/aaacarolina">
                <img src="/icons/ig@2x.png" />
              </a>
              <a href="https://go.carolinas.aaa.com">
                <img src="/icons/gomag@2x.png" />
              </a>
              <a href="https://carolinas.aaa.com/downloadapp/">
                <img src="/icons/aaa-app@2x.png" />
              </a>
            </Social>
          ) : null}
        </Copyright>
      </FooterContainer>
    </>
  );
};

export default Footer;
